import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { MAX_ATTACHMENT_SIZE_BYTES, VIDEO_ATTACHMENT_MIMES } from '@wix/ai-scheduling-assistant-constants';



// Every file type is accepted, on all three surfaces and at the mint (`mediaUploadUrlEndpointBuilder.ts`).
// What the model cannot read reaches it as a note with the file's name, type, size and URL, so the agent
// can fetch, convert or forward it. The mint picks the name WixMP stores it under; see
// `wixmpSafeUploadIdentity`. A browser that cannot name a file's type gives `File.type === ''`.
const UNKNOWN_MIME = 'application/octet-stream';

// Accept attribute for <input type="file">: empty, so the picker offers every file.
export const ATTACHMENT_ACCEPT = '';

// Accept attribute for the CAMERA input (`capture`): the two kinds a camera can produce, and nothing
// else — a document type here would put "Choose File" back inside a row that promises the camera.
// Naming the three video containers rather than `video/*` is what keeps an `.avi`-producing camera app
// off a path the mint would refuse; it is also what tells the browser this input takes video at all,
// which is what puts the PHOTO/VIDEO switch in the camera it opens.
export const CAMERA_CAPTURE_ACCEPT = ['image/*', ...VIDEO_ATTACHMENT_MIMES].join(',');

// Mirrors mobile services/wix-media: rewrites the WixMP thumbnail URL into a
// webp variant so the harness fetches a reasonable-sized image. This is what
// lands on the attachment's `thumbnailUrl`; the file's own url rides `url`.
function toWebpVariantUrl(thumbnailUrl: string): string {
  const base = thumbnailUrl.split('?')[0];
  return `${base}/v1/fit/w_1024,h_1024,q_85/image.webp`;
}

export type UploadFailureKind = 'unsupported' | 'too_large' | 'failed';

export type UploadResult =
  | { ok: true; attachment: MessageAttachment }
  | { ok: false; kind: UploadFailureKind; message: string };

// The image types a browser can render as an avatar, badge or logo. `accept="image/*"` is only a picker
// hint, so an image-only caller passes `{ imageOnly: true }` and anything else is refused before upload.
const RENDERABLE_IMAGE_MIME = /^image\/(jpeg|jpg|png|gif|webp|avif|bmp|svg\+xml|heic|heif)$/i;

export function isRenderableImageMime(mime: string): boolean {
  return RENDERABLE_IMAGE_MIME.test((mime ?? '').split(';')[0].trim());
}

/**
 * Every file type is accepted for CHAT attachments. Callers that save the result as a picture (avatar,
 * Space badge, pack logo, a screenshot claim) pass `imageOnly`, so a non-image is never saved where an
 * image must render.
 */
export async function uploadAttachment(file: File, options: { imageOnly?: boolean } = {}): Promise<UploadResult> {
  if (options.imageOnly && !isRenderableImageMime(file.type)) {
    return { ok: false, kind: 'unsupported', message: 'Type not supported' };
  }
  // #7157: a full-resolution phone photo routinely exceeds the upload cap even at high JPEG quality,
  // and once picked there's no retry that can ever succeed — downscale/compress before the size check
  // and before it's ever handed to the uploader. Non-image files are untouched.
  const uploadFile = file.type.startsWith('image/') ? await downscaledImageFile(file) : file;
  // No size limit of ours. The PUT body is the File itself, which the browser streams from disk.
  const uploadType = uploadFile.type || UNKNOWN_MIME;

  let uploadUrl: string;
  // The name AND type WixMP was told about at mint time. It types a file by the extension and
  // validates the declared type against it, so the server may repair BOTH together (`.md`/`text/markdown`
  // → `.md.txt`/`text/plain`); the PUT must match what was minted or WixMP refuses the pair. The
  // attachment we persist keeps the user's original name and type either way.
  let uploadFileName = uploadFile.name;
  let uploadMimeType = uploadType;
  try {
    const res = await apiFetch(`${API_BASE}/api/upload-url`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileName: uploadFile.name, mimeType: uploadType, size: uploadFile.size }),
    });
    if (!res.ok) {
      // 415 and 413 now only mean WixMP itself refused the file at the mint.
      const kind = res.status === 415 ? 'unsupported' : res.status === 413 ? 'too_large' : 'failed';
      return { ok: false, kind, message: await readErrorMessage(res, 'Upload') };
    }
    const data = (await res.json()) as { uploadUrl?: string; uploadFileName?: string; uploadMimeType?: string };
    if (!data.uploadUrl) return { ok: false, kind: 'failed', message: 'Upload URL missing in response' };
    uploadUrl = data.uploadUrl;
    if (data.uploadFileName) uploadFileName = data.uploadFileName;
    if (data.uploadMimeType) uploadMimeType = data.uploadMimeType;
  } catch (err) {
    return { ok: false, kind: 'failed', message: err instanceof Error ? err.message : String(err) };
  }

  // Direct browser → WixMP PUT. Mirrors mobile's wixMediaService.uploadFile: a
  // `?filename=` query param plus the file body as raw bytes (the browser sets
  // Content-Length and reads the Blob/File natively).
  let payload: { file?: { thumbnailUrl?: string; url?: string; downloadUrl?: string } };
  try {
    const putRes = await fetch(`${uploadUrl}?filename=${encodeURIComponent(uploadFileName)}`, {
      method: 'PUT',
      headers: { 'Content-Type': uploadMimeType },
      body: uploadFile,
    });
    if (!putRes.ok) {
      return { ok: false, kind: 'failed', message: `Upload PUT failed (${putRes.status})` };
    }
    payload = await putRes.json();
  } catch (err) {
    return { ok: false, kind: 'failed', message: err instanceof Error ? err.message : String(err) };
  }

  if (file.type.startsWith('image/')) {
    const thumbnailUrl = payload?.file?.thumbnailUrl;
    // #7157: an image descriptor without a thumbnailUrl (WixMP hasn't generated one yet, or never does
    // for this file) used to fail the whole upload outright, even though the raw file url is right
    // there — the same fallback the non-image branch already had. Fall back to it instead.
    const fileUrl = payload?.file?.url ?? payload?.file?.downloadUrl;
    if (thumbnailUrl) {
      // mimeType follows `url` — describing the file, not the rendition.
      return {
        ok: true,
        attachment: {
          fileName: uploadFile.name,
          mimeType: fileUrl ? uploadFile.type : 'image/webp',
          url: fileUrl ?? toWebpVariantUrl(thumbnailUrl),
          thumbnailUrl: toWebpVariantUrl(thumbnailUrl),
          size: uploadFile.size,
        },
      };
    }
    if (!fileUrl) return { ok: false, kind: 'failed', message: 'WixMP response missing thumbnail/file URL' };
    return {
      ok: true,
      attachment: { fileName: uploadFile.name, mimeType: uploadFile.type, url: fileUrl, thumbnailUrl: fileUrl, size: uploadFile.size },
    };
  }

  const fileUrl = payload?.file?.url ?? payload?.file?.downloadUrl;
  if (!fileUrl) return { ok: false, kind: 'failed', message: 'WixMP response missing file URL' };
  return {
    ok: true,
    attachment: {
      fileName: file.name,
      mimeType: file.type || UNKNOWN_MIME,
      url: fileUrl,
      thumbnailUrl: fileUrl,
      size: uploadFile.size,
    },
  };
}

// The longest side a picked photo is downscaled to before JPEG-encoding (#7157) — a modern phone
// photo's raw pixel dimensions (commonly 4000px+ on the long side) are the main reason it lands over
// the 5 MB model-inline threshold even at reduced JPEG quality. Mirrors native `ChatAttachmentPolicy`.
// Getting under it is what lets the model see the photo; a photo still over it uploads anyway and
// reaches the agent as a URL note.
const MAX_IMAGE_DIMENSION = 2048;
// JPEG quality steps tried, in order, until the encoded size fits under the threshold. The last step is
// accepted even if still over it.
const JPEG_QUALITY_STEPS = [0.7, 0.5, 0.3];

/**
 * Downscale + JPEG-compress an image File client-side so a normal phone photo fits under the upload
 * cap. Returns the original `file` unchanged if the browser can't decode it as an image (never worse
 * than the pre-fix behavior) — the existing size check still applies to whatever this returns.
 */
async function downscaledImageFile(file: File): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file);
    const longestSide = Math.max(bitmap.width, bitmap.height);
    const scale = longestSide > MAX_IMAGE_DIMENSION ? MAX_IMAGE_DIMENSION / longestSide : 1;
    const targetWidth = Math.max(1, Math.round(bitmap.width * scale));
    const targetHeight = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
    bitmap.close?.();

    let best: Blob | null = null;
    for (const quality of JPEG_QUALITY_STEPS) {
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
      if (!blob) continue;
      best = blob;
      if (blob.size <= MAX_ATTACHMENT_SIZE_BYTES) break;
    }
    if (!best) return file;
    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([best], name, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

/** For BI and logs only, never shown: the status and the route's error code. */
async function readErrorMessage(res: Response, label: string): Promise<string> {
  const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
  const code = typeof body?.error === 'string' ? ` ${body.error}` : '';
  return `${label} failed (${res.status}${code})`;
}
